import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export type ApiError<T = unknown, D = unknown> = AxiosError<T, D>;
export type ApiRequestConfig<D = unknown> = AxiosRequestConfig<D>;
export type ApiResponse<T = unknown, D = unknown> = AxiosResponse<T, D>;

export type ApiResultErrorItem = {
	code: string;
	message: string;
};

export type ApiResultError = {
	code: number;
	errors: ApiResultErrorItem[];
	reason: string;
	type: string;
};

export type ApiResult<T> =
	| {
			error?: never;
			success: T;
	  }
	| {
			error: ApiResultError;
			success?: never;
	  };

export enum RequestTarget {
	Gateway = 'api',
	Chat = 'chat'
}

export enum ApiClientErrors {
	GenerateCustomerToken = 'GenerateCustomerToken',
	GetProvisioningToken = 'GetProvisioningToken',
	GetSsoToken = 'GetSsoToken',
	GetTokenPair = 'GetTokenPair',
	LaunchOutboundSso = 'LaunchOutboundSso'
}

export enum Currency {
	USD = 'usd',
	EUR = 'eur',
	GBP = 'gbp'
}

export enum CustomerOrgType {
	Partner = 'partner',
	Customer = 'customer'
}

export enum ConnectorChannel {
	Text = 'text',
	Voice = 'voice',
	Technical = 'technical'
}

export type StorageAdapter = {
	get(key: string): string | null;
	set(key: string, value: string): void;
	remove(key: string): void;
};

export type ApiClientConfig = {
	apiHost: string | null;
	chatApiHost: string | null;
	storage: StorageAdapter;
	onAuthStateChange?: (isAuthorized: boolean) => void;
	onError?: (status: number, reason?: string) => void;
	onUnauthorized?: () => void;
	onOrganizationTokenExpires?: () => void;
};

export type RequestConfig = AxiosRequestConfig & {
	target?: RequestTarget;
	silent?: boolean;
	noErrorMessage?: boolean;
	skipAuth?: boolean;
	skipUnauthorizedHandler?: boolean;
	returnFullResponse?: boolean;
	errorType?: ApiClientErrors;
	__retry?: boolean;
};

export type TokenPair = {
	accessToken: string;
	refreshToken: string;
};

export type UtilRequestConfig = AxiosRequestConfig &
	Omit<RequestConfig, 'url' | 'method' | 'data'>;

export type SessionReportExportRequest = {
	from_datetime?: string;
	to_datetime?: string;
	query?: string;
	connectors?: string[];
	is_active?: boolean;
	session_types?: string[];
	is_test?: boolean;
	is_lead?: boolean;
	is_todo?: boolean;
	is_archived?: boolean;
	is_spam?: boolean;
	is_opportunity?: boolean;
	agent_success?: number;
	agent_success_human?: number;
	channels?: ConnectorChannel[];
	session_lengths?: string[];
};

export type IPagination = {
	page: number;
	per: number;
	total: number;
};

export interface IPaginatedResponse<T> {
	items: T[];
	metadata: IPagination;
}
